import React, { useEffect, useRef, useState } from 'react';
import { FormControl, Select, MenuItem } from '@mui/material';
import { useSnackbar } from '../SnackBar';
import { useProgressBar } from '../ProgressBar';
import { useParams, useNavigate } from 'react-router-dom';
import { usePartnerContext } from '../../components/Context/PartnerContext';
import { useBranchContext } from '../../components/Context/BranchContext';
import { useServiceContext } from '../../components/Context/ServiceContext';

const EditProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { showProgress } = useProgressBar();
    const { ProductCategory, UpdateExistingProduct } = usePartnerContext();
    const { branches } = useBranchContext();
    const { GetProductById } = useServiceContext();

    const [productName, setProductName] = useState('');
    const [manufacturer, setManufacturer] = useState('');
    const [model, setModel] = useState('');
    const [year, setYear] = useState('');
    const [price, setPrice] = useState('');
    const [quantityInStock, setQuantityInStock] = useState('');
    const [reorderLevel, setReorderLevel] = useState('');
    const [inStock, setInStock] = useState(true);
    const [isQuickSelect, setIsQuickSelect] = useState(false);
    const [categoryId, setCategoryId] = useState('');
    const [branchId, setBranchId] = useState('');
    const [productId, setProductId] = useState(null);
    const [loading, setLoading] = useState(true);
    const didFetch = useRef(false);

    useEffect(() => {
        if (didFetch.current) return;
        didFetch.current = true;

        const load = async () => {
            try {
                showProgress(true);
                const data = await GetProductById(id);
                if (!data) {
                    showSnackbar('Product not found', 'error');
                    navigate('/products');
                    return;
                }
                setProductId(data.productId);
                setProductName(data.productName || '');
                setManufacturer(data.manufacturer || '');
                setModel(data.model || '');
                setYear(data.year != null ? String(data.year) : '');
                setPrice(data.price != null ? String(data.price) : '');
                setQuantityInStock(data.quantityInStock != null ? String(data.quantityInStock) : '');
                setReorderLevel(data.reorderLevel != null ? String(data.reorderLevel) : '');
                setCategoryId(data.categoryId != null ? String(data.categoryId) : '');
                setBranchId(data.branchId != null ? String(data.branchId) : '');
                setInStock(data.inStock !== false);
                setIsQuickSelect(data.isQuickSelect === true);
            } catch (err) {
                console.error(err);
                showSnackbar('Failed to load product', 'error');
            } finally {
                showProgress(false);
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const handleSubmit = async () => {
        if (!productName.trim() || !categoryId || !branchId || !price) {
            showSnackbar('Please fill required fields', 'error');
            return;
        }

        showProgress(true);
        try {
            const payload = {
                productId: productId || parseInt(id, 10),
                productName: productName.trim(),
                categoryId: parseInt(categoryId, 10),
                manufacturer: manufacturer.trim(),
                model: model.trim(),
                year: parseInt(year, 10) || 0,
                price: parseFloat(price),
                inStock,
                isQuickSelect,
                branchId: parseInt(branchId, 10),
                quantityInStock: parseInt(quantityInStock, 10) || 0,
                reorderLevel: parseInt(reorderLevel, 10) || 0,
            };

            await UpdateExistingProduct(payload);
            showSnackbar('Product updated successfully', 'success');
            navigate(`/viewproduct/${payload.productId}/false`);
        } catch (err) {
            console.error(err);
            showSnackbar('Failed to update product', 'error');
        } finally {
            showProgress(false);
        }
    };

    if (loading) {
        return <div className="p-4">Loading...</div>;
    }

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <h1>Edit Product</h1>
                <nav>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><a href="/products">Products</a></li>
                        <li className="breadcrumb-item active">Edit Product</li>
                    </ol>
                </nav>
            </div>

            <section className="section">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Product Details</h5>
                                <form>
                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Product Name</label>
                                        <div className="col-sm-6">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={productName}
                                                onChange={(e) => setProductName(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Category</label>
                                        <div className="col-sm-6">
                                            <FormControl fullWidth>
                                                <Select
                                                    value={categoryId}
                                                    onChange={(e) => setCategoryId(e.target.value)}
                                                >
                                                    {Array.isArray(ProductCategory) && ProductCategory.map((cat) => (
                                                        <MenuItem key={cat.id} value={String(cat.id)}>{cat.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Manufacturer</label>
                                        <div className="col-sm-6">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={manufacturer}
                                                onChange={(e) => setManufacturer(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Model</label>
                                        <div className="col-sm-6">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={model}
                                                onChange={(e) => setModel(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Year</label>
                                        <div className="col-sm-6">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={year}
                                                onChange={(e) => setYear(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Price</label>
                                        <div className="col-sm-6">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={price}
                                                onChange={(e) => setPrice(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Branch</label>
                                        <div className="col-sm-6">
                                            <FormControl fullWidth>
                                                <Select
                                                    value={branchId}
                                                    onChange={(e) => setBranchId(e.target.value)}
                                                >
                                                    {Array.isArray(branches) && branches.map((branch) => (
                                                        <MenuItem key={branch.id} value={String(branch.id)}>{branch.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Quantity In Stock</label>
                                        <div className="col-sm-6">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={quantityInStock}
                                                onChange={(e) => setQuantityInStock(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Change Frequency(KM)</label>
                                        <div className="col-sm-6">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={reorderLevel}
                                                onChange={(e) => setReorderLevel(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">In Stock</label>
                                        <div className="col-sm-6 mt-2">
                                            <input
                                                type="checkbox"
                                                checked={inStock}
                                                onChange={(e) => setInStock(e.target.checked)}
                                            /> Available
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <label className="col-sm-2 col-form-label">Quick Select</label>
                                        <div className="col-sm-6 mt-2">
                                            <input
                                                type="checkbox"
                                                checked={isQuickSelect}
                                                onChange={(e) => setIsQuickSelect(e.target.checked)}
                                                id="editProductQuickSelect"
                                            />{' '}
                                            <label htmlFor="editProductQuickSelect">Show as quick select in partner app</label>
                                        </div>
                                    </div>

                                    <div className="col-12 d-flex justify-content-end align-items-end gap-2">
                                        <button type="button" className="btn btn-secondary px-3 py-2" onClick={() => navigate(`/viewproduct/${id}/false`)}>
                                            Cancel
                                        </button>
                                        <button type="button" className="btn btn-primary px-3 py-2" onClick={handleSubmit}>
                                            Save
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default EditProduct;
